// Copyright ©️ 2026 eVolve MEP, LLC

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { type MessageId, type Message, formatDate } from '@evolvemep/api-ts';
import { ActionIcon, Card, Flex, Indicator, Loader, Text, useMantineTheme } from '@mantine/core';
import { notifications } from '@mantine/notifications';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { AgGridStyleTooltip } from 'components/Mantine/AgGridStyleTooltip';
import { isNotNil } from 'helpers/isNotNil';

import { useMessages } from './MessagesContext';

type Props = {
  message: Message;
  currentlyViewing: MessageId | null;
  setCurrentlyViewing: (messageId: MessageId | null) => void;
  canArchive: boolean;
  refreshAllMessages: () => void;
};

export const MessagePreview = ({
  message,
  currentlyViewing,
  setCurrentlyViewing,
  canArchive,
  refreshAllMessages,
}: Props) => {
  const theme = useMantineTheme();
  const ref = useRef<HTMLDivElement>(null);
  const { updateMessage, deleteMessage } = useMessages();
  const [saving, setSaving] = useState(false);
  const [read, setRead] = useState(isNotNil(message.accessedOn));

  const selected = useMemo(() => currentlyViewing === message.messageId, [currentlyViewing, message.messageId]);

  useEffect(() => {
    if (selected) ref.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [selected]);

  useEffect(() => {
    if (selected && !read) {
      void updateMessage(message.messageId, 'Read').then(() => {
        setRead(true);
      });
    }
  }, [selected, read, message.messageId, updateMessage]);

  const doDelete = useCallback(() => {
    setSaving(true);
    deleteMessage(message.messageId)
      .then(() => {
        notifications.show({
          title: 'Deleted',
          message: 'Message successfully deleted',
          color: 'green',
        });
        refreshAllMessages();
        setCurrentlyViewing(null);
      })
      .catch(() => setSaving(false));
  }, [deleteMessage, message.messageId, refreshAllMessages, setCurrentlyViewing]);

  const doArchive = useCallback(() => {
    setSaving(true);
    updateMessage(message.messageId, 'Archive')
      .then(() => {
        notifications.show({
          title: 'Archived',
          message: 'Message successfully archived',
          color: 'green',
        });
        refreshAllMessages();
        setCurrentlyViewing(null);
      })
      .catch(() => setSaving(false));
  }, [message.messageId, refreshAllMessages, setCurrentlyViewing, updateMessage]);

  return (
    <Indicator disabled={read} position="middle-start" size={16} key={message.messageId}>
      <Card
        ref={ref}
        shadow="sm"
        radius="sm"
        withBorder
        bg={selected ? 'gray.3' : undefined}
        style={{
          flex: '0 0 auto',
          userSelect: 'none',
          ...(selected
            ? {
                borderColor: 'gray',
              }
            : {}),
        }}
        onClick={() => {
          setCurrentlyViewing(message.messageId);
        }}
      >
        <Text>
          {message.messagePriorityCode === 'HIGH' && (
            <AgGridStyleTooltip label="High priority" openDelay={0} withArrow>
              <EvolveIcon
                spanProps={{
                  style: {
                    marginTop: 3.5,
                    marginRight: 3,
                  },
                }}
                inline
                icon="Alert"
                color={`${theme.primaryColor}.8`}
                size="sm"
              />
            </AgGridStyleTooltip>
          )}
          {message.subject}
        </Text>
        <Text size="sm" c="dimmed">
          {formatDate(message.createdOn, 'MMMM dd, yyyy')}
        </Text>

        {selected && (
          <>
            {saving ? (
              <Flex justify="right">
                <Loader size="sm" />
              </Flex>
            ) : (
              <Flex gap="md" justify="right">
                {canArchive && (
                  <AgGridStyleTooltip label="Archive">
                    <ActionIcon variant="light" onClick={doArchive}>
                      <EvolveIcon icon="Archive" />
                    </ActionIcon>
                  </AgGridStyleTooltip>
                )}
                <AgGridStyleTooltip label="Delete">
                  <ActionIcon variant="light" onClick={doDelete}>
                    <EvolveIcon icon="Delete" />
                  </ActionIcon>
                </AgGridStyleTooltip>
              </Flex>
            )}
          </>
        )}
      </Card>
    </Indicator>
  );
};
